import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import './Footer.css';

const PRIVACY_POLICY = `Privacy Policy

Effective Date: January 1, 2026

MarginPro ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our platform.

Information We Collect
We collect information you provide directly to us, such as when you create an account, complete a questionnaire, or contact us for support. This may include your name, email address, phone number, and financial profile information used solely for the purpose of generating personalized guidance.

How We Use Your Information
We use the information we collect to operate and improve our platform, personalize your experience, send transactional communications, and provide customer support. We do not sell your personal data to third parties.

Data Security
We implement appropriate technical and organizational security measures to protect your information against unauthorized access, alteration, disclosure, or destruction.

Your Rights
Depending on your location, you may have rights to access, correct, or delete your personal information. To exercise these rights, please contact us at privacy@marginpro.io.

Changes to This Policy
We may update this Privacy Policy from time to time. We will notify you of any material changes by updating the effective date at the top of this page.

Contact Us
If you have questions about this Privacy Policy, please contact us at privacy@marginpro.io.`;

const TERMS_OF_SERVICE = `Terms of Service

Effective Date: January 1, 2026

Welcome to MarginPro. By accessing or using our platform, you agree to be bound by these Terms of Service.

Use of the Platform
MarginPro provides financial education and decision-support tools for business purposes. The guidance provided is informational only and does not constitute legal, tax, or investment advice. You are solely responsible for any decisions made based on the platform's output.

Account Responsibilities
You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You agree to notify us immediately of any unauthorized use of your account.

Intellectual Property
All content, features, and functionality on MarginPro are the exclusive property of MarginPro and its licensors. You may not reproduce, distribute, or create derivative works without our express written permission.

Limitation of Liability
To the fullest extent permitted by law, MarginPro shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the platform.

Termination
We reserve the right to suspend or terminate your account at any time for violation of these Terms or for any other reason at our sole discretion.

Governing Law
These Terms shall be governed by the laws of the State of Delaware, without regard to its conflict of law provisions.

Contact Us
For questions about these Terms, contact us at legal@marginpro.io.`;

const Modal = ({ title, content, onClose }) => {
  const handleKey = useCallback((e) => {
    if (e.key === 'Escape') onClose();
  }, [onClose]);

  useEffect(() => {
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [handleKey]);

  return (
    <div className="modal-overlay" onClick={onClose} role="dialog" aria-modal="true" aria-label={title}>
      <div className="modal-panel" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">{title}</h2>
          <button className="modal-close" onClick={onClose} aria-label="Close modal">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path d="M13.5 4.5L4.5 13.5M4.5 4.5l9 9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
            </svg>
          </button>
        </div>
        <div className="modal-body">
          {content.split('\n\n').map((para, i) => {
            const isHeading = para.length < 60 && !para.includes('.');
            return isHeading
              ? <h3 key={i} className="modal-section-heading">{para}</h3>
              : <p key={i} className="modal-para">{para}</p>;
          })}
        </div>
      </div>
    </div>
  );
};

const Footer = () => {
  const [modal, setModal] = useState(null);

  const openPrivacy = () => setModal({ title: 'Privacy Policy', content: PRIVACY_POLICY });
  const openTerms = () => setModal({ title: 'Terms of Service', content: TERMS_OF_SERVICE });

  const year = new Date().getFullYear();

  return (
    <>
      <footer className="footer">
        <div className="container footer__inner">
          {/* Brand col */}
          <div className="footer__brand">
            <Link to="/" className="footer__logo">
              <span className="navbar__logo-mark footer__logo-mark">M</span>
              <span className="footer__logo-text">Margin<span className="gold-text">Pro</span></span>
            </Link>
            <p className="footer__tagline">Know your numbers before you move.</p>
            <p className="footer__tagline footer__tagline--sub">Business finance intelligence for operators, founders, and project managers.</p>
          </div>

          {/* Link cols */}
          <div className="footer__cols">
            <div className="footer__col">
              <p className="footer__col-title">Product</p>
              <a href="#features" className="footer__link">Features</a>
              <a href="#pricing" className="footer__link">Pricing</a>
              <a href="#how-it-works" className="footer__link">How It Works</a>
              <a href="#" className="footer__link footer__link--coming">Use Case Library</a>
            </div>
            <div className="footer__col">
              <p className="footer__col-title">Account</p>
              <Link to="/login" className="footer__link">Log In</Link>
              <Link to="/chat" className="footer__link footer__link--coming">Dashboard</Link>
            </div>
            <div className="footer__col">
              <p className="footer__col-title">Company</p>
              <button className="footer__link footer__link--btn" onClick={openPrivacy}>Privacy Policy</button>
              <button className="footer__link footer__link--btn" onClick={openTerms}>Terms of Service</button>
              <a href="#" className="footer__link footer__link--coming">About</a>
            </div>
            <div className="footer__col">
              <p className="footer__col-title">Contact</p>
              <a href="mailto:hello@marginpro.io" className="footer__link">hello@marginpro.io</a>
              <a href="mailto:support@marginpro.io" className="footer__link">Support</a>
              <a href="mailto:partners@marginpro.io" className="footer__link">Partnerships</a>
              <div className="footer__social">
                <a href="#" className="footer__social-link" aria-label="LinkedIn" title="LinkedIn">
                  <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
                    <path d="M2 1a1 1 0 100 2 1 1 0 000-2zM1 4.5h2v9H1v-9zm4 0h1.9v1.2h.1c.3-.5 1-1.4 2.5-1.4 2.6 0 3.1 1.7 3.1 4V13.5H11V8.7c0-.9 0-2.1-1.3-2.1-1.3 0-1.5 1-1.5 2v4.9H6.1l-.1-9z" fill="currentColor"/>
                  </svg>
                </a>
                <a href="#" className="footer__social-link" aria-label="X / Twitter" title="X">
                  <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
                    <path d="M11.6 1.5h2L9.1 7l5.4 7H9.8L6.3 9.7 2.3 14H.3l5-5.9L.2 1.5h4.5l3.2 4.3L11.6 1.5zm-.7 11.2h1.1L4.1 2.6H2.9l8 10.1z" fill="currentColor"/>
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className="footer__bottom">
          <div className="container footer__bottom-inner">
            <p className="footer__copyright">© {year} MarginPro. All rights reserved.</p>
            <div className="footer__legal-links">
              <button className="footer__link footer__link--btn footer__link--sm" onClick={openPrivacy}>Privacy</button>
              <span className="footer__divider">·</span>
              <button className="footer__link footer__link--btn footer__link--sm" onClick={openTerms}>Terms</button>
            </div>
          </div>
        </div>
      </footer>

      {modal && <Modal title={modal.title} content={modal.content} onClose={() => setModal(null)} />}
    </>
  );
};

export default Footer;
